import { hc } from "hono/client";
//#region src/client.ts
/**
* Adapter that mimics the legacy `@locuslabs/lliam-client` swagger response
* shape `{ ok, status, body }` so consumer call-sites can migrate w/ minimal
* churn. Body is parsed as JSON; non-JSON or unparseable responses yield
* `body: undefined`. Consumers are responsible for narrowing the body type.
*/
async function unwrapLliamResponse(res) {
	const contentType = res.headers.get("content-type") ?? "";
	let body;
	if (contentType.includes("application/json")) try {
		body = await res.json();
	} catch {
		body = void 0;
	}
	return {
		ok: res.ok,
		status: res.status,
		body
	};
}
/**
* Convenience helper mirroring legacy `lliam.getDerivedPolicyForToken()`.
* Backend `/policy-for-token` already returns the derived policy server-side,
* so this is a thin wrapper for migration ergonomics. The body matches
* {@link PolicyForTokenResponseType} on a 2xx response.
*/
async function getDerivedPolicyForToken(client, stage = "v3") {
	return unwrapLliamResponse(await client[":stage"]["policy-for-token"].$get({ param: { stage } }));
}
const DEFAULT_BASE_URL = "http://localhost:3000";
const PROD_BASE_URL = "https://lliam.locuslabs.com";
function createLliamClient({ token, baseUrl = DEFAULT_BASE_URL, userAgent, fetcher = fetch } = {}) {
	return hc(baseUrl, {
		headers: {
			...token ? { "x-api-key": token } : {},
			...userAgent ? { "user-agent": userAgent } : {}
		},
		fetch: fetcher
	});
}
function createLliamProdClient({ token, userAgent, fetcher = fetch } = {}) {
	return createLliamClient({
		token,
		baseUrl: PROD_BASE_URL,
		userAgent,
		fetcher
	});
}
function createLliamHelper(opts) {
	const stage = opts.stage ?? "prod";
	const client = createLliamClient({
		token: opts.token,
		baseUrl: opts.baseUrl ?? PROD_BASE_URL,
		userAgent: opts.userAgent,
		fetcher: opts.fetcher
	});
	return {
		client,
		getDerivedPolicyForToken: async () => {
			const response = await client[":stage"]["policy-for-token"].$get({ param: { stage } });
			if (!response.ok) return {
				ok: false,
				status: response.status,
				body: void 0
			};
			const body = await response.json();
			return {
				ok: true,
				status: response.status,
				body
			};
		},
		getMe: async () => {
			const response = await client[":stage"].me.$get({ param: { stage } });
			if (!response.ok) return {
				ok: false,
				status: response.status,
				body: void 0
			};
			const body = await response.json();
			return {
				ok: true,
				status: response.status,
				body
			};
		},
		authenticate: async () => {
			const response = await client[":stage"].authenticate.$get({ param: { stage } });
			return {
				ok: response.ok,
				status: response.status
			};
		}
	};
}
//#endregion
export { createLliamClient, createLliamHelper, createLliamProdClient, getDerivedPolicyForToken, unwrapLliamResponse };

//# sourceMappingURL=client.mjs.map